// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable react-hooks/immutability */
import _ from "lodash";
import React, { useMemo } from "react";
import { Handles, Rail, Slider, Ticks, Tracks } from "react-compound-slider";
import AFPlot from "../Plotting/AFPlotly";
import { Handle, SliderRail, Tick, Track } from "./sliderComponents";

type HistogramProps = {
	buckets: number[] | { [key: number]: number };
	min?: number;
	max?: number;
	count?: number;
	skipLow?: boolean;
	skipHigh?: boolean;
	slider?: boolean;
	ticks?: boolean;
	onSliderChange?: (v: readonly number[]) => void;
	onSliderUpdate?: (v: readonly number[]) => void;
	rootStyle?: React.CSSProperties;
	sliderValues?: [number, number];
	colorscale?: [number, string][];
};

const Histogram = ({
	buckets = [],
	min = 0,
	max = 255,
	count = 256,
	skipLow = false,
	skipHigh = false,
	slider = false,
	ticks = false,
	onSliderChange = () => {},
	onSliderUpdate = () => {},
	rootStyle = {},
	sliderValues = [0, 255],
	colorscale = undefined,
}: HistogramProps) => {
	if (Object.keys(buckets).length === count) {
		if (skipLow) {
			buckets[min] = 0;
		}

		if (skipHigh) {
			buckets[max] = 0;
		}
	}

	const chartData = useMemo(() => {
		const data = _.map(Object.entries(buckets), ([i, v]) => ({ x: Number(i), y: v }));
		const traces: Plotly.Data[] = [
			{
				x: data.map((p) => p.x),
				y: data.map((p) => p.y),
				marker: {
					color: data.map((p) => p.x),
					cmin: sliderValues[0],
					cmax: sliderValues[1],
					colorscale: colorscale,
				},
				type: "bar",
			},
		];

		return traces;
	}, [buckets, sliderValues, colorscale]);

	const chartLayout: Partial<Plotly.Layout> = {
		showlegend: false,
		// height: 150,
		// width: Number(rootStyle.width) ?? 340,
		autosize: true,
		hovermode: false,
		clickmode: "none",
		dragmode: false,
		margin: {
			b: 0,
			l: 0,
			r: 0,
			t: 0,
		},
		xaxis: {
			showgrid: false,
		},
		yaxis: {
			showgrid: false,
		},
		paper_bgcolor: "#fff0",
		plot_bgcolor: "#fff0",
		barmode: "overlay",
		bargap: 0,
	};

	const chartConfig = {
		displayModeBar: false, // Hides the modebar
		scrollZoom: false, // Disables scroll zooming
		dragMode: false, // Disables panning,
		responsive: true,
	};

	const sliderStyle = {
		position: "relative",
		bottom: 0,
	};

	// const throttledOnSliderUpdate = useCallback(
	// 	_.debounce((v: readonly number[]) => onSliderUpdate(v), 100),
	// 	[onSliderUpdate]
	// );

	return (
		<div style={{ paddingBottom: ticks ? 20 : 0, marginBottom: 6, ...rootStyle }}>
			{/* {Object.keys(buckets).length > 0 && (
				<CanvasJSChart options={chartOptions} containerProps={{ height: "inherit", width: "inherit" }} />
			)} */}
			{Object.keys(buckets).length > 0 && (
				<AFPlot data={chartData} style={{ width: "100%", height: "100%" }} layout={chartLayout} config={chartConfig} />
			)}
			{slider && (
				<Slider
					mode={2}
					step={(max - min) / (count - 1)}
					domain={[min, max]}
					rootStyle={sliderStyle}
					onChange={(v) => {
						onSliderChange(v);
					}}
					onUpdate={(v) => {
						onSliderUpdate(v);
						// throttledOnSliderUpdate(v);
					}}
					values={sliderValues}
				>
					<Rail>
						{({ getRailProps }) => (
							<SliderRail
								getRailProps={getRailProps}
								outerStyle={{ height: 160, transform: "translate(0%, calc(-100% + 10px))" }}
							/>
						)}
					</Rail>
					<Handles>
						{({ handles, getHandleProps }) => (
							<div className="slider-handles">
								{handles.map((handle) => (
									<Handle key={handle.id} handle={handle} domain={[min, max]} getHandleProps={getHandleProps} />
								))}
							</div>
						)}
					</Handles>
					<Tracks left={false} right={false}>
						{({ tracks, getTrackProps }) => (
							<div className="slider-tracks">
								{tracks.map(({ id, source, target }) => (
									<Track key={id} source={source} target={target} getTrackProps={getTrackProps} />
								))}
							</div>
						)}
					</Tracks>
					{ticks && (
						<Ticks values={Array.from({ length: 18 }, (_, i) => i * 15)}>
							{({ ticks }) => (
								<div className="slider-ticks">
									{ticks.map((tick) => (
										<Tick key={tick.id} tick={tick} count={ticks.length} />
									))}
								</div>
							)}
						</Ticks>
					)}
				</Slider>
			)}
		</div>
	);
};

export default Histogram;
